import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../config/constant';
import MasterService from '../../services/MasterService';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import './AddVehicle.css';

const toCamelCase = (str) => {
    if (!str) return '';
    return str.toLowerCase()
             .replace(/_/g, ' ')
             .replace(/\b\w/g, char => char.toUpperCase());
}

const AddVehicle = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeCategory, setActiveCategory] = useState('Photos');
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [blurPlate, setBlurPlate] = useState(false);
    const [isBlurring, setIsBlurring] = useState(false);
    const [showBlurSelector, setShowBlurSelector] = useState(false);
    const [blurAreas, setBlurAreas] = useState([]);
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectionStart, setSelectionStart] = useState(null);
    const [currentSelection, setCurrentSelection] = useState(null);
    const [documentConfigs, setDocumentConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editLeadId, setEditLeadId] = useState(null);
    const [editDealerId, setEditDealerId] = useState(null);
    const [mmvData, setMmvData] = useState({
        make: [],
        model: [],
        version: []
    });
    const [selectedMake, setSelectedMake] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedVariant, setSelectedVariant] = useState('');
    const [selectedDealer, setSelectedDealer] = useState('');
    const [dealers, setDealers] = useState([]);
    const [carColors, setCarColors] = useState([]);
    const [otherMake, setOtherMake] = useState('');
    const [otherModel, setOtherModel] = useState('');
    const [otherVariant, setOtherVariant] = useState(''); 

    // Check for edit mode and set flags
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const leadId = searchParams.get('lead_id');
        const dealerId = searchParams.get('dealer_id');
        
        if (leadId && dealerId) {
            setIsEditMode(true);
            setEditLeadId(leadId);
            setEditDealerId(dealerId);
        }
        
        // Load initial data
        fetchDocumentConfigs(); 
        fetchMMVData();
        fetchDealers();
        fetchCarColors();
    }, [location.search]);

    // Fetch vehicle details after configs are loaded and in edit mode
    useEffect(() => {
        if (isEditMode && editLeadId && editDealerId && documentConfigs.length > 0) {
            fetchVehicleDetails(editLeadId, editDealerId);
        }
    }, [isEditMode, editLeadId, editDealerId, documentConfigs]);

    const fetchDocumentConfigs = async () => {
        setLoading(true);
        try {
            const result = await MasterService.getDocumentConfigs();
            
            if (result.status === 200) {
                setDocumentConfigs(result.data);
            }
        } catch (error) {
            console.error('Error fetching document configs:', error);
            toast.error('Failed to load document configurations');
        } finally {
            setLoading(false);
        }
    };

    const fetchMMVData = async () => {
        try {
            const result = await MasterService.getMMV();
            
            if (result.data.status === 200) {
                setMmvData(result.data.data);
            }
        } catch (error) {
            console.error('Error fetching MMV data:', error);
            toast.error('Failed to load vehicle data');
        }
    };

    const fetchCarColors = async () => {
        try {
            const response = await MasterService.getCarColors();
            if (response.data?.data) {
                setCarColors(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching car colors:', error);
            toast.error('Failed to fetch car colors');
        }
    };

    const fetchDealers = async () => {
        setLoading(true);
        try {
            const response = await MasterService.getDealers();
            if (response.status === 200) {
                setDealers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching dealers:', error);
            toast.error('Failed to load dealers');
        } finally {
            setLoading(false);
        }
    };

    const fetchVehicleDetails = async (leadId, dealerId) => {
        try {
            setLoading(true);
            const result = await MasterService.getVehicleDetails(leadId, dealerId);
            console.log("**********",result)
            if (result.status === 200 && result.data) {
                const vehicleData = result.data?.data;
                const basicDetails = vehicleData.basic_details || {};
                
                // Convert reg_date from "Feb 2026" to "2026-02" format for month input
                let formattedRegDate = '';
                if (basicDetails.reg_date) {
                    const months = {
                        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
                        'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
                        'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
                    };
                    const [monthName, year] = basicDetails.reg_date.split(' ');
                    const monthNum = months[monthName];
                    if (monthNum && year) {
                        formattedRegDate = `${year}-${monthNum}`;
                    }
                    // Debug logging to check parsing
                    console.log('Parsing reg_date:', basicDetails.reg_date, '->', formattedRegDate);
                }
                
                // Convert tax_expiry from "23/04/2026" to "2026-04-23" format
                let formattedTaxExpiry = '';
                if (basicDetails.tax_expiry) {
                    const [day, month, year] = basicDetails.tax_expiry.split('/');
                    formattedTaxExpiry = `${year}-${month}-${day}`;
                }
                
                // Set vehicle details
                setVehicleDetails({
                    ...vehicleData.basic_details,
                    year: basicDetails.make_year, 
                    tax_expiry: formattedTaxExpiry,
                    reg_date: formattedRegDate,
                    oto_base_price: vehicleData?.oto_base_price,
                    dealer_id: vehicleData.dealer_id,
                    lead_id: vehicleData.lead_id,
                    status: vehicleData.basic_details?.auction_status
                });
                
                // Set make/model/variant if available
                if (basicDetails.make_id === 0) {
                    setSelectedMake('0');
                    setOtherMake(basicDetails.make_name || '');
                } else if (basicDetails.make_name) {
                    setSelectedMake(basicDetails.make_name);
                }
                
                if (basicDetails.model_id === 0) {
                    setSelectedModel('0');
                    setOtherModel(basicDetails.model_name || '');
                } else if (basicDetails.model_name) {
                    setSelectedModel(basicDetails.model_name);
                }
                
                if (basicDetails.variant_id == 0) {
                    setSelectedVariant('0');
                    setOtherVariant(basicDetails.variant_name || '');
                } else if (basicDetails.variant_name) {
                    setSelectedVariant(basicDetails.variant_name);
                }
                if (vehicleData.dealer_id) {
                    setSelectedDealer(vehicleData.dealer_id.toString());
                }

                // Handle existing documents from vehicle_images and return latest document
                
                let latestUploadedDocument = null;
                
                if (vehicleData.vehicle_images && vehicleData.vehicle_images.length > 0) {
                    const existingDocs = {};
                    
                    // Sort by ID descending to get the latest documents first
                    const sortedImages = [...vehicleData.vehicle_images].sort((a, b) => b.id - a.id);
                    
                    sortedImages.forEach(doc => {
                        // Find the corresponding config by matching doc_name with config.name
                        const allConfigs = [...photoConfigs, ...documentConfigsOnly];
                        const config = allConfigs.find(c => c.name === doc.doc_name);
                        
                        if (config) {
                            const category = photoConfigs.includes(config) ? 'Photos' : 'Documents';
                            
                            if (!existingDocs[category]) {
                                existingDocs[category] = {};
                            }
                            
                            // Initialize array for this config name if not exists
                            if (!existingDocs[category][config.name]) {
                                existingDocs[category][config.name] = [];
                            }
                            
                            // Create a mock file object for display purposes
                            const mockFile = {
                                name: doc.doc_name || config.name,
                                url: doc.doc_url,
                                watermarkUrl: doc.doc_url_watermark,
                                doc_id: doc.doc_id,
                                id: doc.id
                            };
                            
                            existingDocs[category][config.name].push(mockFile);
                            
                            // Track the latest uploaded document
                            if (!latestUploadedDocument) {
                                latestUploadedDocument = {
                                    category: config.name,
                                    file: mockFile,
                                    categoryName: category
                                };
                            }
                        } else {
                            // No config found for doc_name
                        }
                    });
                    
                    setUploadedFiles(existingDocs);
                    
                    return latestUploadedDocument;
                } else {
                    setUploadedFiles({ Photos: {}, Documents: {} });
                    return null;
                }
            }
        } catch (error) {
            console.error('Error fetching vehicle details:', error);
            toast.error('Failed to load vehicle details');
        } finally {
            setLoading(false);
        }
    };

    // Separate photos and documents based on API response
    const photoConfigs = documentConfigs.filter(config => 
        !config.name.includes('STNK') && 
        !config.name.includes('BPKB') && 
        !config.name.includes('Faktur') && 
        !config.name.includes('KTP') && 
        !config.name.includes('NPWP')
    );

    const documentConfigsOnly = documentConfigs.filter(config => 
        config.name.includes('STNK') || 
        config.name.includes('BPKB') || 
        config.name.includes('Faktur') || 
        config.name.includes('KTP') || 
        config.name.includes('NPWP')
    );

    // Vehicle details form state
    const [vehicleDetails, setVehicleDetails] = useState({
        make_id: '',
        model_id: '',
        variant_id: '',
        year: '',
        tax_expiry: '',
        accidental: '',
        flooded: '',
        mileage: '',
        cylinder: '',
        colour: '',
        km_driven: '',
        plate_number: '',
        reg_date: '',
        deal_price: '',
        oto_base_price: '',
        dealer_id: '',
        status: '',
        remarks: ''
    });

    // Uploaded files state
    const [uploadedFiles, setUploadedFiles] = useState({
        Photos: {},
        Documents: {}
    });

    const handleBack = () => {
        navigate('/vehicles');
    };

    // Blur area selection handlers
    const handleMouseDown = (e) => {
        console.log('Mouse down event triggered', { showBlurSelector });
        if (!showBlurSelector) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        console.log('Selection started at:', { x, y });
        
        setIsSelecting(true);
        setSelectionStart({ x, y });
        setCurrentSelection({ x, y, width: 0, height: 0 });
    };

    const handleMouseMove = (e) => {
        if (!isSelecting || !showBlurSelector) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const rect = e.currentTarget.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        
        const width = currentX - selectionStart.x;
        const height = currentY - selectionStart.y;
        
        const newSelection = {
            x: width < 0 ? currentX : selectionStart.x,
            y: height < 0 ? currentY : selectionStart.y,
            width: Math.abs(width),
            height: Math.abs(height)
        };
        
        console.log('Selection updated:', newSelection);
        setCurrentSelection(newSelection);
    };

    const handleMouseUp = (e) => {
        console.log('Mouse up event triggered', { isSelecting, currentSelection });
        if (!isSelecting || !currentSelection) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        if (currentSelection.width > 10 && currentSelection.height > 10) {
            console.log('Adding blur area:', currentSelection);
            setBlurAreas([...blurAreas, currentSelection]);
        } else {
            console.log('Selection too small, ignoring');
        }
        
        setIsSelecting(false);
        setCurrentSelection(null);
        setSelectionStart(null);
    };

    const handleBlurPlate = async () => {
        if (!selectedPhoto || selectedPhoto.isEmpty || !selectedPhoto.file) {
            toast.error('Please select an image first');
            return;
        }
        
        try {
            setIsBlurring(true);
            toast.info('Blurring plate number...');
            
            const apiUrl = `${API_BASE_URL}/image-blur/blur-plate`;
            // Debug coordinates being sent
            console.log('=== FRONTEND COORDINATE DEBUG ===');
            console.log('Selected photo URL:', selectedPhoto.file.url);
            console.log('Blur areas to send:', blurAreas);
            
            // Check if we have the image element to get dimensions
            const imgElement = document.querySelector('.preview-image');
            let scaledBlurAreas = blurAreas;
            
            if (imgElement) {
                const naturalWidth = imgElement.naturalWidth;
                const naturalHeight = imgElement.naturalHeight;
                const displayWidth = imgElement.width;
                const displayHeight = imgElement.height;
                
                console.log('Displayed image dimensions:', {
                    naturalWidth,
                    naturalHeight,
                    displayWidth,
                    displayHeight
                });
                
                // Scale coordinates from display size to original image size
                const scaleX = naturalWidth / displayWidth;
                const scaleY = naturalHeight / displayHeight;
                
                console.log('Coordinate scaling factors:', { scaleX, scaleY });
                
                scaledBlurAreas = blurAreas.map(area => ({
                    x: Math.round(area.x * scaleX),
                    y: Math.round(area.y * scaleY),
                    width: Math.round(area.width * scaleX),
                    height: Math.round(area.height * scaleY)
                }));
                
                console.log('Scaled blur areas:', scaledBlurAreas);
            }
            
            const requestBody = JSON.stringify({
                imageUrl: selectedPhoto.file.url,
                plateArea: scaledBlurAreas.length > 0 ? scaledBlurAreas : undefined
            });
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
                },
                body: requestBody
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error Response:', errorText);
                throw new Error(`API request failed: ${response.status} - ${errorText}`);
            }
            
            const result = await response.json();
            console.log('API Response:', result);
            
            if (result.success) {
                // Convert data URL to blob
                const blobResponse = await fetch(result.dataUrl);
                const blob = await blobResponse.blob();
                
                // Get original file info
                const originalFileName = selectedPhoto.file.name;
                const fileExtension = originalFileName.split('.').pop().toLowerCase();
                const mimeType = fileExtension === 'png' ? 'image/png' : 'image/jpeg';
                
                // Ensure filename has proper extension for upload validation
                let fileNameWithExtension = originalFileName;
                if (!originalFileName.includes('.')) {
                    // Original file has no extension, add one based on MIME type
                    const extension = mimeType === 'image/png' ? 'png' : 'jpg';
                    fileNameWithExtension = `${originalFileName}.${extension}`;
                }
                
                // Create file from blob with correct format for upload
                const blurredFile = new File([blob], fileNameWithExtension, { type: mimeType });
                
                
                // Upload blurred image to S3 using correct format
                const formData = new FormData();
                formData.append('upload_type', 'dif_auction');
                formData.append('lead_id', editLeadId);
                formData.append('images', blurredFile);
                
                for (let [key, value] of formData.entries()) {
                    console.log(`${key}:`, value);
                }
                
                const uploadResponse = await MasterService.uploadFilesToS3(
                    formData, 
                    { 'Content-Type': 'multipart/form-data' }
                );
                
                if (uploadResponse.data.status === 200) {
                    // Update file in state with new signed URL
                    const newFileData = uploadResponse.data.data[0];
                    
                    // Update document paths in database
                    try {
                        const updateResponse = await MasterService.updateDocumentPaths(selectedPhoto.file.id, newFileData.file_url);
                        console.log('Document paths updated successfully in database:', updateResponse);
                        
                        // Update selected photo with new URL from API response
                        if (updateResponse.data && updateResponse.data.data) {
                            setSelectedPhoto(prev => ({
                                ...prev,
                                file: {
                                    ...prev.file,
                                    url: updateResponse.data.data.signed_doc_path || updateResponse.data.data.doc_path || newFileData.file_url,
                                    watermarkUrl: updateResponse.data.data.signed_doc_path || updateResponse.data.data.doc_path || newFileData.file_url,
                                }
                            }));
                        }
                    } catch (updateError) {
                        console.error('Failed to update document paths:', updateError);
                        toast.error('Failed to update document paths in database');
                    }
                    
                    setUploadedFiles(prev => ({
                        ...prev,
                        [activeCategory]: {
                            ...prev[activeCategory],
                            [selectedPhoto.category]: prev[activeCategory][selectedPhoto.category].map(file => 
                                file.id === selectedPhoto.file.id ? { ...file, ...newFileData } : file
                            )
                        }
                    }));
                    
                    // Update selected photo to show new blurred image with signed URL
                    setSelectedPhoto(prev => ({
                        ...prev,
                        file: { ...prev.file, ...newFileData }
                    }));
                    
                    // Fetch vehicle details again to ensure blurred images are properly loaded
                    await fetchVehicleDetails(editLeadId, vehicleDetails.dealer_id || editDealerId);
                    
                    toast.success('Plate number blurred and updated successfully!');
                } else {
                    toast.error('Failed to upload blurred image');
                }
            } else {
                toast.error(result.message || 'Failed to blur image');
            }
        } catch (error) {
            console.error('Blur error:', error);
            toast.error('Failed to blur image');
        } finally {
            setIsBlurring(false);
            setShowBlurSelector(false);
            setBlurAreas([]);
        }
    };

    const startBlurSelection = () => {
        if (!selectedPhoto || selectedPhoto.isEmpty || !selectedPhoto.file) {
            toast.error('Please select an image first');
            return;
        }
        setShowBlurSelector(true);
        setBlurAreas([]);
        toast.info('Draw rectangles over areas to blur, then click "Apply Blur"');
    };

    const cancelBlurSelection = () => {
        setShowBlurSelector(false);
        setBlurAreas([]);
        setCurrentSelection(null);
        setIsSelecting(false);
    };

    const removeBlurArea = (index) => {
        console.log('Removing blur area at index:', index);
        console.log('Current blur areas:', blurAreas);
        const newBlurAreas = blurAreas.filter((_, i) => i !== index);
        console.log('New blur areas:', newBlurAreas);
        setBlurAreas(newBlurAreas);
    };

    const handleFileUpload = async (config, files) => {
        // Check if vehicle details are submitted first (only allow uploads in edit mode)
        if (!editLeadId) {
            toast.error('First submit vehicle details to upload documents.');
            return;
        }

        const fileArray = Array.from(files);
        
        // Validate file types
        for (const file of fileArray) {
            const fileType = file.type;
            if (config.is_gcloud && !fileType.startsWith('image/')) {
                toast.error('Please upload valid image files only.');
                return;
            }
            if (!config.is_gcloud && !fileType.startsWith('image/') && !fileType.includes('pdf')) {
                toast.error('Please upload valid image or PDF files.');
                return;
            }
        }

        // Check if all document categories are uploaded for final submission
        const checkIfAllDocumentsUploaded = () => {
            const allConfigs = [...photoConfigs, ...documentConfigsOnly];
            const uploadedCategories = new Set();
            
            Object.keys(uploadedFiles).forEach(category => {
                Object.keys(uploadedFiles[category]).forEach(docName => {
                    if (uploadedFiles[category][docName]) {
                        uploadedCategories.add(docName);
                    }
                });
            });
            
            // Add current category being uploaded
            uploadedCategories.add(config.name);
            
            return allConfigs.every(config => uploadedCategories.has(config.name));
        };

        // Upload files to S3
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('upload_type', 'dif_auction');
            formData.append('lead_id', editLeadId);
            
            fileArray.forEach((file) => {
                formData.append('images', file);
            });

            const uploadResponse = await MasterService.uploadFilesToS3(
                formData, 
                { 'Content-Type': 'multipart/form-data' }
            );

            if (uploadResponse.data.status === 200) {
                // Determine if this is final submission
                const isFinalSubmission = checkIfAllDocumentsUploaded() ? "1" : "0";
                
                // Save document information
                const documentData = {
                    lead_id: parseInt(editLeadId),
                    dealer_id: parseInt(vehicleDetails.dealer_id || editDealerId),
                    is_final_submission: isFinalSubmission,
                    documents: uploadResponse.data.data.map((file, index) => ({
                        doc_id: config.id,
                        doc_path: file.file_url,
                        doc_path_watermark: file.file_url,
                        latitude: "0",
                        longitude: "0",
                        description: `${config.name} - Document ${index + 1}`
                    })),
                    source: 'web',
                };

                const saveResponse = await MasterService.saveDocuments(documentData);
                
                if (saveResponse.status === 200) {
                    // First fetch vehicle details to get signed URLs for all documents
                    const latestDocument = await fetchVehicleDetails(editLeadId, vehicleDetails.dealer_id || editDealerId);
                    
                    // After fetchVehicleDetails, set selectedPhoto with the latest uploaded document
                    if (activeCategory && latestDocument) {
                        // Use the latest uploaded document from the API response
                        setSelectedPhoto({ 
                            category: latestDocument.category, 
                            file: latestDocument.file 
                        });
                        setCurrentImageIndex(0);
                    }
                    
                    toast.success(`${config.name} uploaded successfully!`);
                } else {
                    toast.error('Failed to save document information');
                }
            } else {
                toast.error('Failed to upload files');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Upload failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileRemove = (categoryName, fileIndex = null) => {
        setUploadedFiles(prev => {
            const categoryFiles = prev[activeCategory] || {};
            const filesArray = categoryFiles[categoryName] || [];
            
            if (fileIndex !== null && filesArray.length > 1) {
                // Remove specific file from array
                const newFilesArray = filesArray.filter((_, index) => index !== fileIndex);
                return {
                    ...prev,
                    [activeCategory]: {
                        ...prev[activeCategory],
                        [categoryName]: newFilesArray
                    }
                };
            } else {
                // Remove all files for this category
                return {
                    ...prev,
                    [activeCategory]: {
                        ...prev[activeCategory],
                        [categoryName]: []
                    }
                };
            }
        });
    };

    const handleDownload = async (configName, file) => {
        if (!file || !file.url) {
            toast.error('No file available for download');
            return;
        }

        try {
            const response = await fetch(file.url);
            if (!response.ok) {
                throw new Error('Failed to fetch file');
            }

            const blob = await response.blob();

            // Extract extension from URL (strip query params)
            const cleanUrl = file.url.split('?')[0];
            const urlParts = cleanUrl.split('.');
            const fileExtension = urlParts.length > 1 ? urlParts.pop() : 'jpg';

            const sanitizedName = configName.replace(/[^a-zA-Z0-9]/g, '_');
            saveAs(blob, `VID${editLeadId}_${sanitizedName}.${fileExtension}`);

            toast.success(`${configName} downloaded successfully!`);
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download file. Please try again.');
        }
    };

    const handleRemoveDocument = async () => {
        if (!selectedPhoto || !selectedPhoto.file || !selectedPhoto.file.id) {
            toast.error('No document selected for removal');
            return;
        }

        try {
            setLoading(true);
            
            // Call remove documents API with the document ID
            const response = await MasterService.removeDocuments([selectedPhoto.file.id]);
            
            if (response.status === 200) {
                // Remove document from state
                const categoryName = selectedPhoto.category;
                const categoryFiles = uploadedFiles[activeCategory] || {};
                const filesArray = categoryFiles[categoryName] || [];
                
                // Filter out the removed document
                const updatedFiles = filesArray.filter(file => file.id !== selectedPhoto.file.id);
                
                // Update state
                setUploadedFiles(prev => ({
                    ...prev,
                    [activeCategory]: {
                        ...prev[activeCategory],
                        [categoryName]: updatedFiles
                    }
                }));
                
                // Clear selected photo if no more files in this category
                if (updatedFiles.length === 0) {
                    setSelectedPhoto(null);
                } else {
                    // Set to first remaining file
                    setSelectedPhoto({ category: categoryName, file: updatedFiles[0] });
                    setCurrentImageIndex(0);
                }
                
                toast.success('Document removed successfully!');
            } else {
                toast.error('Failed to remove document');
            }
        } catch (error) {
            console.error('Remove error:', error);
            toast.error('Failed to remove document. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoClick = (category, file) => {
        // Get the files array for the category
        const categoryFiles = uploadedFiles[activeCategory] || {};
        const filesArray = categoryFiles[category] || [];
        
        // Always set the selected photo, even if empty
        if (filesArray.length > 0) {
            // Use the first file from the array
            const fileWithUrl = filesArray[0];
            setSelectedPhoto({ category, file: fileWithUrl });
            setCurrentImageIndex(0); // Reset to first image when selecting new category
        } else {
            // Set empty state for categories with no pictures
            setSelectedPhoto({ category, file: null, isEmpty: true });
        }
    };

    // Get all images for the current selected category
    const getCurrentCategoryImages = () => {
        if (!selectedPhoto || selectedPhoto.isEmpty) return [];
        
        const categoryFiles = uploadedFiles[activeCategory] || {};
        const images = [];
        
        // Get the files array for the selected category name
        const filesArray = categoryFiles[selectedPhoto.category] || [];
        
        // Create image objects for each file in the array
        filesArray.forEach((file, index) => {
            if (file) {
                images.push({ name: selectedPhoto.category, file, index });
            }
        });
        
        return images;
    };

    // Update selected photo when current image index changes
    useEffect(() => {
        if (selectedPhoto) {
            const images = getCurrentCategoryImages();
            if (images.length > 0 && images[currentImageIndex]) {
                setSelectedPhoto(prev => ({
                    ...prev,
                    file: images[currentImageIndex].file
                }));
            }
        }
    }, [currentImageIndex, selectedPhoto?.category]);

    const handleInputChange = (field, value) => {
        // Handle cascading dropdown logic and store IDs
        if (field === 'make') {
            setSelectedMake(value);
            setSelectedModel('');
            setSelectedVariant('');
            const selectedMakeData = mmvData.make.find(m => m.make === value);
            setVehicleDetails(prev => ({
                ...prev,
                make_id: selectedMakeData?.id || '',
                model_id: '',
                variant_id: ''
            }));
        } else if (field === 'model') {
            setSelectedModel(value);
            setSelectedVariant('');
            const selectedModelData = mmvData.model.find(m => m.m === value);
            setVehicleDetails(prev => ({
                ...prev,
                model_id: selectedModelData?.id || '',
                variant_id: ''
            }));
        } else if (field === 'variant') {
            setSelectedVariant(value);
            const selectedVariantData = mmvData.version.find(v => v.vn === value);
            setVehicleDetails(prev => ({
                ...prev,
                variant_id: selectedVariantData?.vn_id || ''
            }));
        } else if (field === 'dealer') {
            setSelectedDealer(value);
            const selectedDealerData = dealers?.find(d => d.id.toString() === value);
            setVehicleDetails(prev => ({
                ...prev,
                dealer_id: selectedDealerData?.id || ''
            }));
        } else if (field === 'other_make') {
            setOtherMake(value);
        } else if (field === 'other_model') {
            setOtherModel(value);
        } else if (field === 'other_variant') {
            setOtherVariant(value);
        } else {
            // Handle all other fields including reg_date
            // For reg_date, store as YYYY-MM format for proper display
            setVehicleDetails(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    // Filter models based on selected make
    const getFilteredModels = () => {
        if (!selectedMake) return [];
        const selectedMakeData = mmvData.make.find(m => m.make === selectedMake);
        return selectedMakeData ? mmvData.model.filter(m => m.mk_id === selectedMakeData.id) : [];
    };

    // Filter variants based on selected model
    const getFilteredVariants = () => {
        if (!selectedMake || !selectedModel) return [];
        const selectedMakeData = mmvData.make.find(m => m.make === selectedMake);
        const selectedModelData = mmvData.model.find(m => m.m === selectedModel && m.mk_id === selectedMakeData?.id);
        return selectedModelData ? mmvData.version.filter(v => v.md_id === selectedModelData?.id) : [];
    };

    // Generate year options from 2000 to 2026
    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = 2000; year <= currentYear; year++) {
            years.push(year);
        }
        return years.reverse(); // Most recent year first
    };

    // Determine if fields should be enabled based on vehicle status
    const isFieldsEnabled = vehicleDetails?.status === '' || !['AUCTION_APPROVED','BID_RECEIVED','SOLD'].includes(vehicleDetails.status);

    const handleSave = async () => {
        if (['RESUBMISSION', 'QC_REJECTED'].includes(vehicleDetails.status) && !vehicleDetails.remarks?.trim()) {
            toast.error('Remarks are required for Resubmission and QC Rejected status');
            return;
        }

        try {
            setLoading(true);

            // Convert reg_date from YYYY-MM to YYYY-MM-DD format for API
            let formattedRegDate = '';
            if (vehicleDetails.reg_date) {
                const [year, month] = vehicleDetails.reg_date.split('-');
                formattedRegDate = `${month}/${year}`;
            }
            
            // Map form data to API payload format
            const vehicleDataToSave = {
                ...(isEditMode && { id: parseInt(editLeadId) }), // Include lead_id for updates
                dealer_id: vehicleDetails.dealer_id || null,
                make_id: selectedMake === '0' ? 0 : (vehicleDetails.make_id || null),
                model_id: selectedModel === '0' ? 0 : (vehicleDetails.model_id || null),
                variant_id: selectedVariant === '0' ? 0 : (vehicleDetails.variant_id || null),
                make_year: vehicleDetails.year || null,
                tax_expiry: vehicleDetails.tax_expiry || '',
                accidental: vehicleDetails.accidental || "no",
                flooded: vehicleDetails.flooded || "no",
                mileage: parseFloat(vehicleDetails.mileage) || 0,
                cylinder: parseInt(vehicleDetails.cylinder) || 0,
                colour: vehicleDetails.colour || '',
                km_driven: parseInt(vehicleDetails.km_driven) || 0,
                deal_price: parseInt(vehicleDetails.deal_price) || 0,
                oto_base_price: parseInt(vehicleDetails.oto_base_price) || 0,
                reg_date: formattedRegDate,
                plate_number: vehicleDetails.plate_number || '',
                auction_status: vehicleDetails.status || null,
                remarks: vehicleDetails.remarks || '',
                source: 'web',
                // Include other fields when Others is selected
                other_make: selectedMake === '0' ? otherMake : '',
                other_model: selectedModel === '0' ? otherModel : '',
                other_variant: selectedVariant === '0' ? otherVariant : '',
            };

            console.log('Saving vehicle:', vehicleDataToSave);

            // Call saveVehicleDetails API
            const saveResponse = await MasterService.saveVehicleDetails(vehicleDataToSave);
            console.log("**************",saveResponse)
            if (saveResponse.status === 200 || saveResponse.data?.status === 200) {
                toast.success('Vehicle details saved successfully!');

                // Get the saved vehicle ID from response or use existing editLeadId
                const savedVehicleId = saveResponse.data?.data?.lead_id || editLeadId;
                const savedDealerId = vehicleDetails.dealer_id || editDealerId;

                // Navigate to edit mode with the saved/updated vehicle
                navigate(`/vehicles/add?lead_id=${savedVehicleId}&dealer_id=${savedDealerId}`);
            } else {
                const errorMsg = saveResponse?.message || saveResponse?.data?.message || 'Failed to save vehicle details';
                toast.error(errorMsg);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Failed to save vehicle. Please try again.';
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const renderUploadSection = () => {
        const configs = activeCategory === 'Photos' ? photoConfigs : documentConfigsOnly;
        const files = uploadedFiles[activeCategory] || {};
        
        return (
            <div className="upload-section">
                {!editLeadId && (
                    <div className="upload-locked-message">
                        <div className="locked-icon">🔒</div>
                        <p>First submit vehicle details to upload documents</p>
                    </div>
                )}
                <div className="category-list">
                    {configs.map(config => (
                        <div key={config.id} className={`category-item ${!editLeadId ? 'locked' : ''}`}>
                            {/* <div className="category-header"> */}
                                {/* <img 
                                    src={config.doc_icon_url} 
                                    alt={config.name}
                                    className="category-icon"
                                /> */}
                              
                                {/* {!editLeadId && <span className="lock-indicator">🔒</span>} */}
                            {/* </div> */}
                            {/* <div className="upload-info">
                                <small className="upload-limits">
                                    Min: {config.min_upload}, Max: {config.max_upload}
                                </small>
                            </div> */}
                            <div className="upload-area">
                                  <span 
                                    className="category-name clickable"
                                    onClick={() => handlePhotoClick(config.name, files[config.name])}
                                >
                                    {config.name}
                                    {config.is_required && <span className="required">*</span>}
                                    <span>({files[config.name] && files[config.name].length > 0 && files[config.name]?.length || 0}/{config.max_upload})</span>
                                </span>
                                {files[config.name] && files[config.name].length >= config.max_upload ? (
                                    <></>
                                ) : (
                                    <label className={`upload-label ${!editLeadId ? 'disabled' : ''}`}>
                                        <input
                                            type="file"
                                            accept={config.is_gcloud ? 'image/*' : 'application/pdf,image/*'}
                                            multiple={config.max_upload > 1}
                                            onChange={(e) => handleFileUpload(config, e.target.files)}
                                            style={{ display: 'none' }}
                                            disabled={!editLeadId}
                                        />
                                        <div 
                                            className={` ${!editLeadId ? 'locked' : ''}`}
                                           
                                        >
                                            {!editLeadId ? (
                                                <>
                                                    <span className="lock-icon">🔒</span>
                                                    <small>Locked</small>
                                                </>
                                            ) : (
                                                <>
                                                    {/* <img 
                                                        src={config.doc_sample_icon} 
                                                        alt="Sample"
                                                        className="sample-icon"
                                                    /> */}
                                                    <i className='ic-upload'></i>
                                                    {/* <small>
                                                        Upload {files[config.name] && files[config.name].length > 0 
                                                            ? `(${files[config.name].length}/${config.max_upload})`
                                                            : config.max_upload > 1 
                                                                ? `(Max ${config.max_upload})`
                                                                : '(Required)'
                                                        }
                                                    </small> */}
                                                </>
                                            )}
                                        </div>
                                    </label>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };


    
    return (
        <div className="add-vehicle-container">
            <div className="header">
                <div className="arrow-bck" onClick={handleBack}>
                   <i className='ic-arrow_back'></i>
                </div>
                <h1>{isEditMode ? 'Edit Vehicle Details' : 'Add New Vehicle'}</h1>
            </div>

            <div className="main-content">
                {/* Left Sidebar - Categories */}
                <div className="sidebar">
                    <div className="category-tabs">
                        <button
                            className={`tab ${activeCategory === 'Photos' ? 'active' : ''}`}
                            onClick={() => setActiveCategory('Photos')}
                        >
                            Photos
                        </button>
                        <button
                            className={`tab ${activeCategory === 'Documents' ? 'active' : ''}`}
                            onClick={() => setActiveCategory('Documents')}
                        >
                            Documents
                        </button>
                    </div>
                    {renderUploadSection()}
                </div>

                {/* Center - Image Preview */}
                <div className="image-preview">
                    {selectedPhoto ? (
                        <div className="preview-container">
                            <div className="image-slider">
                                {selectedPhoto.isEmpty ? (
                                    <div className="no-preview">
                                        <div className="preview-placeholder">
                                            <span>📷</span>
                                            <p>No pictures uploaded</p>
                                            <small>Category: {selectedPhoto.category}</small>
                                        </div>
                                    </div>
                                ) : selectedPhoto.file && (selectedPhoto.file.url || selectedPhoto.file.file_url) ? (
                                    <div className="preview-container">
                                        <img 
                                        src={selectedPhoto.file.watermarkUrl ||selectedPhoto.file.url || selectedPhoto.file.file_url}
                                        alt={selectedPhoto.category}
                                        className="preview-image"
                                        style={{ cursor: showBlurSelector ? 'crosshair' : 'default' }}
                                    />
                                        
                                        {showBlurSelector && (
                                            <div 
                                                className="blur-overlay"
                                                onMouseDown={handleMouseDown}
                                                onMouseMove={handleMouseMove}
                                                onMouseUp={handleMouseUp}
                                                onMouseLeave={handleMouseUp}
                                            >
                                                {/* Render existing blur areas */}
                                                {blurAreas.map((area, index) => (
                                                    <div
                                                        key={index}
                                                        className="blur-area"
                                                        style={{
                                                            left: area.x,
                                                            top: area.y,
                                                            width: area.width,
                                                            height: area.height
                                                        }}
                                                    >
                                                        <button 
                                                            className="remove-area-btn"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                console.log('Remove button clicked for index:', index);
                                                                removeBlurArea(index);
                                                            }}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                                
                                                {/* Render current selection */}
                                                {currentSelection && (
                                                    <div
                                                        className="blur-area current-selection"
                                                        style={{
                                                            left: currentSelection.x,
                                                            top: currentSelection.y,
                                                            width: currentSelection.width,
                                                            height: currentSelection.height
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="no-preview">
                                        <div className="preview-placeholder">
                                            <span>📷</span>
                                            <p>No image URL available</p>
                                            <small>Category: {selectedPhoto.category}</small>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Slider Controls */}
                                {getCurrentCategoryImages().length > 1 && (
                                    <div className="slider-controls">
                                        <button 
                                            className="slider-btn prev"
                                            onClick={() => setCurrentImageIndex(prev => 
                                                prev === 0 ? getCurrentCategoryImages().length - 1 : prev - 1
                                            )}
                                            disabled={getCurrentCategoryImages().length <= 1}
                                        >
                                            ‹
                                        </button>
                                        <div className="image-counter">
                                            {currentImageIndex + 1} / {getCurrentCategoryImages().length}
                                        </div>
                                        <button 
                                            className="slider-btn next"
                                            onClick={() => setCurrentImageIndex(prev => 
                                                prev === getCurrentCategoryImages().length - 1 ? 0 : prev + 1
                                            )}
                                            disabled={getCurrentCategoryImages().length <= 1}
                                        >
                                            ›
                                        </button>
                                    </div>
                                )}
                            </div>
                            
                            <div className="image-controls">
                                <button 
                                    className="btn-primary"
                                    onClick={() => selectedPhoto && !selectedPhoto.isEmpty && handleDownload(selectedPhoto.category, selectedPhoto.file)}
                                    disabled={!selectedPhoto || selectedPhoto.isEmpty}
                                >
                                    Download
                                </button>
                                <button 
                                    className="btn-line"
                                    onClick={handleRemoveDocument}
                                    disabled={!selectedPhoto || !selectedPhoto.file || !selectedPhoto.file.id || selectedPhoto.isEmpty || ['AUCTION_APPROVED','BID_RECEIVED','SOLD'].includes(vehicleDetails.status)}
                                >
                                    Remove
                                </button>
                                {!showBlurSelector ? (
                                    <button 
                                        className="btn-primary"
                                        onClick={startBlurSelection}
                                        disabled={!selectedPhoto || selectedPhoto.isEmpty || !selectedPhoto.file || isBlurring}
                                    >
                                        Blur Plate Number
                                    </button>
                                ) : (
                                    <div className="blur-controls">
                                        <button 
                                            className="btn-primary"
                                            onClick={handleBlurPlate}
                                            disabled={blurAreas.length === 0 || isBlurring}
                                        >
                                            {`Apply Blur (${blurAreas.length})`}
                                        </button>
                                        <button 
                                            className="btn-line"
                                            onClick={cancelBlurSelection}
                                            disabled={isBlurring}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="no-preview">
                            <div className="preview-placeholder">
                                <span>📷</span>
                                <p>Select a photo to preview</p>
                                {loading && <div className="loading-spinner add-vehicle-loader"></div>}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Sidebar - Vehicle Details */}
                <div className="vehicle-details">
                    <h2>Vehicle Details</h2>
                    {/* IF Vehicle Banner */}
                    {editLeadId && vehicleDetails.is_if_vehicle === '1' && (
                        <div className="if-vehicle-banner">
                            <div className="if-vehicle-tag">
                                <span className="if-vehicle-text">IF Vehicle</span>
                                {vehicleDetails.if_vehicle_status && (
                                    <span className="if-vehicle-status">{vehicleDetails.if_vehicle_status}</span>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="details-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Make</label>
                                <Select
                                    value={selectedMake ? { value: selectedMake, label: selectedMake === '0' ? 'Others' : selectedMake } : null}
                                    onChange={(selectedOption) => handleInputChange('make', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: '', label: 'Select Make' },
                                        ...mmvData.make.map(make => ({ value: make.make, label: make.make })),
                                        { value: '0', label: 'Others' }
                                    ]}
                                    placeholder="Select Make"
                                    isClearable
                                    isSearchable
                                    isDisabled={!isFieldsEnabled}
                                />
                            </div>
                            {selectedMake === '0' && (
                                <div className="form-group">
                                    <label>Other Make</label>
                                    <input
                                        type="text"
                                        value={otherMake}
                                        onChange={(e) => handleInputChange('other_make', e.target.value)}
                                        placeholder="Enter other make"
                                        disabled={!isFieldsEnabled}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Model</label>
                                <Select
                                    value={selectedModel ? { value: selectedModel, label: selectedModel === '0' ? 'Others' : selectedModel } : null}
                                    onChange={(selectedOption) => handleInputChange('model', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: '', label: 'Select Model' },
                                        ...getFilteredModels().map(model => ({ value: model.m, label: model.m })),
                                        { value: '0', label: 'Others' }
                                    ]}
                                    placeholder="Select Model"
                                    isClearable
                                    isSearchable
                                    isDisabled={!selectedMake || !isFieldsEnabled}
                                />
                            </div>
                            {selectedModel === '0' && (
                                <div className="form-group">
                                    <label>Other Model</label>
                                    <input
                                        type="text"
                                        value={otherModel}
                                        onChange={(e) => handleInputChange('other_model', e.target.value)}
                                        placeholder="Enter other model"
                                        disabled={!isFieldsEnabled}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Variant</label>
                                <Select
                                    value={selectedVariant ? { value: selectedVariant, label: selectedVariant === '0' ? 'Others' : selectedVariant } : null}
                                    onChange={(selectedOption) => handleInputChange('variant', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: '', label: 'Select Variant' },
                                        ...getFilteredVariants().map(variant => ({ value: variant.vn, label: variant.vn })),
                                        { value: '0', label: 'Others' }
                                    ]}
                                    placeholder="Select Variant"
                                    isClearable
                                    isSearchable
                                    isDisabled={!selectedModel || !isFieldsEnabled}
                                />
                            </div>
                            {selectedVariant === '0' && (
                                <div className="form-group">
                                    <label>Other Variant</label>
                                    <input
                                        type="text"
                                        value={otherVariant}
                                        onChange={(e) => handleInputChange('other_variant', e.target.value)}
                                        placeholder="Enter other variant"
                                        disabled={!isFieldsEnabled}
                                    />
                                </div>
                            )}
                        </div>

                        <div className='form-row'>
                            <div className="form-group">
                                <label>Year</label>
                                <Select
                                    value={vehicleDetails.year ? { value: vehicleDetails.year, label: vehicleDetails.year } : null}
                                    onChange={(selectedOption) => handleInputChange('year', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: '', label: 'Select Year' },
                                        ...getYearOptions().map(year => ({ value: year, label: year }))
                                    ]}
                                    placeholder="Select Year"
                                    isClearable
                                    isSearchable
                                    isDisabled={!isFieldsEnabled}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Tax Expiry</label>
                                <DatePicker
                                    selected={vehicleDetails.tax_expiry ? new Date(vehicleDetails.tax_expiry) : null}
                                    onChange={(date) => {
                                        const formattedDate = date ? date.toLocaleDateString('en-CA').split('T')[0] : '';
                                        handleInputChange('tax_expiry', formattedDate);
                                    }}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Select Tax Expiry Date"
                                    className="form-control"
                                    disabled={!isFieldsEnabled}
                                />
                            </div>
                            <div className="form-group">
                                <label>Mileage</label>
                                <input
                                    type="number"
                                    value={vehicleDetails.mileage}
                                    onChange={(e) => handleInputChange('mileage', e.target.value)}
                                    disabled={!isFieldsEnabled}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Accidental</label>
                                <Select
                                    value={vehicleDetails.accidental ? { value: vehicleDetails.accidental, label: vehicleDetails.accidental === 'yes' ? 'Yes' : 'No' } : null}
                                    onChange={(selectedOption) => handleInputChange('accidental', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: 'no', label: 'No' },
                                        { value: 'yes', label: 'Yes' }
                                    ]}
                                    placeholder="Select Option"
                                    isClearable
                                    isSearchable
                                    isDisabled={!isFieldsEnabled}
                                />
                            </div>
                            <div className="form-group">
                                <label>Flooded</label>
                                <Select
                                    value={vehicleDetails.flooded ? { value: vehicleDetails.flooded, label: vehicleDetails.flooded === 'yes' ? 'Yes' : 'No' } : null}
                                    onChange={(selectedOption) => handleInputChange('flooded', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: 'no', label: 'No' },
                                        { value: 'yes', label: 'Yes' }
                                    ]}
                                    placeholder="Select Option"
                                    isClearable
                                    isSearchable
                                    isDisabled={!isFieldsEnabled}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Number of Cylinders</label>
                                <input
                                    type="number"
                                    value={vehicleDetails.cylinder}
                                    onChange={(e) => handleInputChange('cylinder', e.target.value)}
                                    disabled={!isFieldsEnabled}
                                />
                            </div>
                            <div className="form-group">
                                <label>Color</label>
                                <Select
                                    value={vehicleDetails.colour ? { value: vehicleDetails.colour, label: carColors.find(c => c.value === vehicleDetails.colour)?.label || vehicleDetails.colour } : null}
                                    onChange={(selectedOption) => handleInputChange('colour', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: '', label: 'Select Color' },
                                        ...carColors.map(color => ({ value: color.value, label: color.label }))
                                    ]}
                                    placeholder="Select Color"
                                    isClearable
                                    isSearchable
                                    isDisabled={!isFieldsEnabled}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Kms Driven</label>
                                <input
                                    type="number"
                                    value={vehicleDetails.km_driven}
                                    onChange={(e) => handleInputChange('km_driven', e.target.value)}
                                    disabled={!isFieldsEnabled}
                                />
                            </div>
                            <div className="form-group">
                                <label>Plate Number</label>
                                <input
                                    type="text"
                                    value={vehicleDetails.plate_number}
                                    onChange={(e) => handleInputChange('plate_number', e.target.value)}
                                    disabled={!isFieldsEnabled}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Registration Date</label>
                                <DatePicker
                                    selected={vehicleDetails.reg_date ? new Date(vehicleDetails.reg_date + '-01') : null}
                                    onChange={(date) => {
                                        const formattedDate = date ? date.toLocaleDateString('en-CA').slice(0, 7) : '';
                                        handleInputChange('reg_date', formattedDate);
                                    }}
                                    dateFormat="MM/yyyy"
                                    showMonthYearPicker
                                    placeholderText="Select Registration Date"
                                    className="form-control"
                                    disabled={!isFieldsEnabled}
                                />
                            </div>
                            <div className="form-group">
                                <label>Deal Price</label>
                                <input
                                    type="number"
                                    value={vehicleDetails.deal_price}
                                    onChange={(e) => handleInputChange('deal_price', e.target.value)}
                                    disabled={!isFieldsEnabled}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>OTO Base Price</label>
                                <input
                                    type="number"
                                    value={vehicleDetails.oto_base_price}
                                    onChange={(e) => handleInputChange('oto_base_price', e.target.value)}
                                    disabled={!isFieldsEnabled}
                                />
                            </div>
                            <div className="form-group">
                                <label>Dealer</label>
                                <Select
                                    value={selectedDealer ? { value: selectedDealer, label: dealers?.find(d => d.id.toString() === selectedDealer)?.organization + ` (GCD${selectedDealer})` || selectedDealer } : null}
                                    onChange={(selectedOption) => handleInputChange('dealer', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: '', label: 'Select Dealer' },
                                        ...(dealers?.map(dealer => ({ value: dealer.id.toString(), label: `${dealer.organization} (GCD${dealer.id})` })) || [])
                                    ]}
                                    placeholder="Select Dealer"
                                    isClearable
                                    isSearchable
                                    isDisabled={!isFieldsEnabled}
                                />
                            </div>
                        </div>

                        {editLeadId && (
                            <>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Status</label>
                                <Select
                                    value={vehicleDetails.status ? { value: vehicleDetails.status, label: toCamelCase(vehicleDetails.status) } : null}
                                    onChange={(selectedOption) => handleInputChange('status', selectedOption ? selectedOption.value : '')}
                                    options={[
                                        { value: '', label: 'Select Status' },
                                        { value: 'SOLD', label: 'Sold' },
                                        { value: 'UNSOLD', label: 'Unsold' },
                                        { value: 'RESUBMISSION', label: 'Re-submission' },
                                        { value: 'AUCTION_APPROVED', label: 'Auction Approved' },
                                        { value: 'QC_REJECTED', label: 'QC Rejected' },
                                        { value: 'WITHDRAWN', label: 'Withdrawn' },
                                        { value: 'REJECTED', label: 'Rejected' }
                                    ]}
                                    placeholder="Select Status"
                                    isClearable
                                    isSearchable
                                />
                            </div>
                            <div className="form-group">
                                {/* Empty placeholder for alignment */}
                            </div>
                        </div>

                        <div className="form-group full-width">
                            <label>Remarks</label>
                            <textarea
                                value={vehicleDetails.remarks}
                                onChange={(e) => handleInputChange('remarks', e.target.value)}
                                rows="3"
                                disabled={!isFieldsEnabled}
                            />
                        </div>
                            </>
                        )}

                        <button className="btn-primary max-w-200" onClick={handleSave}>
                            Save
                        </button>
                    </div>
                </div>
            </div>
         </div>
    );
};

export default AddVehicle;
